import React from 'react';
import { Checkmark16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import styles from '../irlObservations2026.module.css';
import { DetailHeader, DetailTabs, IrlCounts, mentionIdsFromText } from './shared';

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function initialsFromName(name) {
    const parts = String(name || '')
        .split(/\s+/)
        .filter(Boolean);
    if (!parts.length) return '';
    return parts
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase();
}

function sortedCandidates(candidates) {
    return [...(candidates || [])].filter(member => member?.name).sort((left, right) => String(right.name).length - String(left.name).length);
}

function filterCandidates(candidates, query) {
    const needle = String(query || '').toLowerCase();
    return (candidates || []).filter(member => {
        const name = String(member?.name || '').toLowerCase();
        if (!name) return false;
        if (!needle) return true;
        if (name.startsWith(needle)) return true;
        return name.split(/\s+/).some(part => part.startsWith(needle));
    });
}

function findCompletedMember(afterAt, candidates) {
    const typed = String(afterAt || '');
    if (!typed) return null;
    const names = sortedCandidates(candidates);
    for (const member of names) {
        const name = String(member.name || '');
        if (!typed.toLowerCase().startsWith(name.toLowerCase())) continue;
        const rest = typed.slice(name.length);
        if (rest.length > 0 && !/^[\s.,!?;:)'"]/.test(rest)) continue;
        if (rest.length === 0) {
            const canGrow = names.some(other => {
                const otherName = String(other.name || '').toLowerCase();
                return otherName !== name.toLowerCase() && otherName.startsWith(`${name.toLowerCase()} `);
            });
            if (canGrow) continue;
        }
        return member;
    }
    return null;
}

function resolveMentionQuery(text, caret, candidates) {
    const before = String(text || '').slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0 && !/[\s\n]/.test(before.charAt(at - 1))) return null;
    const afterAt = before.slice(at + 1);
    if (afterAt.includes('\n')) return null;
    const completed = findCompletedMember(afterAt, candidates);
    if (completed) return { start: at, query: completed.name, completed: true, member: completed };
    return { start: at, query: afterAt, completed: false };
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function htmlFromText(text, candidates) {
    return mentionParts(text, candidates)
        .map(part => {
            if (part.type === 'mention') {
                return `<span class="${styles.mentionChip}" data-mention="true">${escapeHtml(part.value)}</span>`;
            }
            return escapeHtml(part.value).replace(/\n/g, '<br>');
        })
        .join('');
}

function plainTextAndCaret(root) {
    const selection = window.getSelection();
    const hasCaret = Boolean(selection && selection.rangeCount && root.contains(selection.anchorNode));
    let text = '';
    let caret = 0;
    let found = !hasCaret;
    const walk = node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const value = node.textContent || '';
            if (!found && node === selection.anchorNode) {
                caret = text.length + selection.anchorOffset;
                found = true;
            }
            text += value;
            return;
        }
        if (node.nodeName === 'BR') {
            if (!found && node === selection.anchorNode) {
                caret = text.length;
                found = true;
            }
            text += '\n';
            return;
        }
        Array.from(node.childNodes || []).forEach(walk);
    };
    walk(root);
    if (!found) caret = text.length;
    if (text.endsWith('\n') && root.lastChild?.nodeName === 'BR' && text.length === 1) {
        text = '';
        caret = 0;
    }
    return { text, caret };
}

function setCaretOffset(root, offset) {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    let remaining = Math.max(0, offset);
    let placed = false;
    const walk = node => {
        if (placed) return;
        if (node.nodeType === Node.TEXT_NODE) {
            const len = (node.textContent || '').length;
            if (remaining <= len) {
                range.setStart(node, remaining);
                range.collapse(true);
                placed = true;
                return;
            }
            remaining -= len;
            return;
        }
        if (node.nodeName === 'BR') {
            if (remaining <= 0) {
                range.setStartAfter(node);
                range.collapse(true);
                placed = true;
                return;
            }
            remaining -= 1;
            return;
        }
        Array.from(node.childNodes || []).forEach(walk);
    };
    walk(root);
    if (!placed) {
        range.selectNodeContents(root);
        range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
}

function mentionParts(text, candidates) {
    const body = String(text || '');
    const names = sortedCandidates(candidates).map(member => member.name);
    if (!body || !names.length) return [{ type: 'text', value: body }];
    const pattern = new RegExp(`(@(?:${names.map(escapeRegExp).join('|')}))(?=$|[\\s.,!?;:)'"])`, 'gi');
    const parts = [];
    let lastIndex = 0;
    let match = pattern.exec(body);
    while (match) {
        if (match.index > lastIndex) parts.push({ type: 'text', value: body.slice(lastIndex, match.index) });
        parts.push({ type: 'mention', value: match[1] });
        lastIndex = match.index + match[1].length;
        match = pattern.exec(body);
    }
    if (lastIndex < body.length) parts.push({ type: 'text', value: body.slice(lastIndex) });
    return parts.length ? parts : [{ type: 'text', value: body }];
}

function CommentBody({ text, candidates, deleted }) {
    if (deleted) return <p className={`${styles.discBody} ${styles.discDeleted}`}>{text}</p>;
    const parts = mentionParts(text, candidates);
    return (
        <p className={styles.discBody}>
            {parts.map((part, index) =>
                part.type === 'mention' ? (
                    <span key={`${part.value}-${index}`} className={styles.mentionChip}>
                        {part.value}
                    </span>
                ) : (
                    <React.Fragment key={`text-${index}`}>{part.value}</React.Fragment>
                ),
            )}
        </p>
    );
}

function MentionField({ value, onChange, candidates, placeholder, className, mentionTrigger, autoFocus, onEscape }) {
    const editorRef = React.useRef(null);
    const wrapRef = React.useRef(null);
    const lastTrigger = React.useRef(mentionTrigger);
    const composingRef = React.useRef(false);
    const lastEmitted = React.useRef(String(value || ''));
    const [menu, setMenu] = React.useState(null);
    const filtered = filterCandidates(candidates, menu?.query);
    const open = Boolean(menu) && !menu?.completed && (candidates || []).length > 0;

    const paint = React.useCallback(
        (text, caret) => {
            const editor = editorRef.current;
            if (!editor) return;
            const html = htmlFromText(text, candidates);
            if (editor.innerHTML !== html) editor.innerHTML = html;
            if (typeof caret === 'number') setCaretOffset(editor, caret);
        },
        [candidates],
    );

    const applyText = React.useCallback(
        (text, caret) => {
            const next = String(text || '');
            const active = resolveMentionQuery(next, caret, candidates);
            let body = next;
            let nextCaret = caret;
            if (active?.completed && active.member?.name) {
                const typed = next.slice(active.start + 1, active.start + 1 + active.member.name.length);
                if (typed !== active.member.name) {
                    body = `${next.slice(0, active.start)}@${active.member.name}${next.slice(active.start + 1 + typed.length)}`;
                    nextCaret = active.start + 1 + active.member.name.length + (caret - (active.start + 1 + typed.length));
                }
            }
            if (body !== lastEmitted.current) {
                lastEmitted.current = body;
                onChange(body);
            }
            paint(body, nextCaret);
            if (!active || active.completed) {
                setMenu(null);
                return;
            }
            setMenu(current => ({
                start: active.start,
                query: active.query,
                completed: false,
                index: current && current.start === active.start ? current.index : 0,
            }));
        },
        [candidates, onChange, paint],
    );

    const pick = React.useCallback(
        member => {
            if (!member?.name) return;
            const editor = editorRef.current;
            const current = editor ? plainTextAndCaret(editor) : { text: String(value || ''), caret: String(value || '').length };
            const start = menu?.start ?? current.caret;
            const before = current.text.slice(0, start);
            const after = current.text.slice(current.caret);
            const inserted = `@${member.name} `;
            const next = `${before}${inserted}${after}`;
            lastEmitted.current = next;
            onChange(next);
            paint(next, before.length + inserted.length);
            setMenu(null);
            editorRef.current?.focus();
        },
        [menu, onChange, paint, value],
    );

    React.useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return undefined;
        const next = String(value || '');
        const current = plainTextAndCaret(editor).text;
        if (current === next) return undefined;
        lastEmitted.current = next;
        paint(next, next.length);
        return undefined;
    }, [paint, value]);

    React.useEffect(() => {
        if (!autoFocus) return undefined;
        editorRef.current?.focus();
        return undefined;
    }, [autoFocus]);

    React.useEffect(() => {
        if (!mentionTrigger || mentionTrigger === lastTrigger.current) return undefined;
        lastTrigger.current = mentionTrigger;
        const editor = editorRef.current;
        const current = editor ? plainTextAndCaret(editor) : { text: String(value || ''), caret: String(value || '').length };
        const next = `${current.text.slice(0, current.caret)}@${current.text.slice(current.caret)}`;
        lastEmitted.current = next;
        onChange(next);
        paint(next, current.caret + 1);
        setMenu({ start: current.caret, query: '', completed: false, index: 0 });
        editor?.focus();
        return undefined;
    }, [mentionTrigger, onChange, paint, value]);

    React.useEffect(() => {
        if (!open) return undefined;
        const close = event => {
            if (!wrapRef.current?.contains(event.target)) setMenu(null);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const readAndApply = () => {
        const editor = editorRef.current;
        if (!editor || composingRef.current) return;
        const current = plainTextAndCaret(editor);
        applyText(current.text, current.caret);
    };

    return (
        <div className={styles.mentionField} ref={wrapRef}>
            <div
                ref={editorRef}
                className={`${className || ''} ${styles.composerEditor}`.trim()}
                contentEditable
                role="textbox"
                aria-multiline="true"
                data-placeholder={placeholder || ''}
                suppressContentEditableWarning
                onInput={readAndApply}
                onClick={readAndApply}
                onKeyUp={readAndApply}
                onCompositionStart={() => {
                    composingRef.current = true;
                }}
                onCompositionEnd={() => {
                    composingRef.current = false;
                    readAndApply();
                }}
                onPaste={event => {
                    event.preventDefault();
                    const pasted = event.clipboardData?.getData('text/plain') || '';
                    const editor = editorRef.current;
                    if (!editor) return;
                    const current = plainTextAndCaret(editor);
                    applyText(`${current.text.slice(0, current.caret)}${pasted}${current.text.slice(current.caret)}`, current.caret + pasted.length);
                }}
                onKeyDown={event => {
                    if (event.key === 'Escape' && !open) {
                        onEscape?.();
                        return;
                    }
                    if (open && event.key === 'ArrowDown' && filtered.length) {
                        event.preventDefault();
                        setMenu(current => ({ ...current, index: ((current?.index || 0) + 1) % filtered.length }));
                        return;
                    }
                    if (open && event.key === 'ArrowUp' && filtered.length) {
                        event.preventDefault();
                        setMenu(current => ({
                            ...current,
                            index: ((current?.index || 0) - 1 + filtered.length) % filtered.length,
                        }));
                        return;
                    }
                    if (open && (event.key === 'Enter' || event.key === 'Tab') && filtered.length) {
                        event.preventDefault();
                        pick(filtered[menu?.index || 0] || filtered[0]);
                        return;
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        setMenu(null);
                        return;
                    }
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        const editor = editorRef.current;
                        if (!editor) return;
                        const current = plainTextAndCaret(editor);
                        applyText(`${current.text.slice(0, current.caret)}\n${current.text.slice(current.caret)}`, current.caret + 1);
                    }
                }}
            />
            {open && (
                <div className={styles.mentionMenu} role="listbox">
                    {filtered.length === 0 ? (
                        <div className={styles.mentionEmpty}>No people match</div>
                    ) : (
                        filtered.map((member, index) => (
                            <button
                                key={member.id}
                                type="button"
                                role="option"
                                aria-selected={index === (menu?.index || 0)}
                                className={index === (menu?.index || 0) ? styles.mentionOptionActive : undefined}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => pick(member)}>
                                {member.name}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function Comment({
    item,
    onReply,
    onSaveEdit,
    onConfirmDelete,
    onCopy,
    copiedCommentId,
    onResolve,
    onReopen,
    viewerMembershipId,
    replyFor,
    replyText,
    onReplyText,
    onSubmitReply,
    onCancelReply,
    mentionCandidates,
    mentionTrigger,
    onMentionTrigger,
    focusCommentId,
    commentPrefix = 'irl-comment',
    isThread,
    busy,
}) {
    const mine = viewerMembershipId && item.authorId === viewerMembershipId;
    const highlighted = String(focusCommentId) === String(item.id);
    const [editing, setEditing] = React.useState(false);
    const [editText, setEditText] = React.useState(item.body || '');
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const replyOpen = replyFor === item.id;

    const startEdit = () => {
        setEditText(item.body || '');
        setEditing(true);
        setConfirmDelete(false);
        if (replyOpen) onCancelReply?.();
    };

    const cancelEdit = () => {
        setEditText(item.body || '');
        setEditing(false);
    };

    const saveEdit = async () => {
        const next = String(editText || '').trim();
        if (!next || busy) return;
        const saved = await onSaveEdit(item, editText, mentionIdsFromText(editText, mentionCandidates));
        if (saved !== false) setEditing(false);
    };

    return (
        <div
            id={`${commentPrefix}-${item.id}`}
            className={`${isThread ? styles.discussionThread : styles.replyItem} ${highlighted ? styles.commentHighlight : ''} ${item.resolved ? styles.threadResolved : ''}`}>
            <div className={isThread ? styles.avatar : `${styles.avatar} ${styles.avatarMuted}`}>{item.initials}</div>
            <div className={styles.discContent}>
                <div className={styles.discMeta}>
                    <span className={isThread ? styles.discAuthor : styles.discAuthorReply}>{item.author}</span>
                    <span className={styles.discTime}>{item.time}</span>
                    {item.edited ? <span className={styles.discTime}> · Edited</span> : null}
                    {item.resolved ? <span className={styles.discTime}> · Resolved</span> : null}
                </div>
                {editing ? (
                    <>
                        <MentionField
                            value={editText}
                            onChange={setEditText}
                            candidates={mentionCandidates}
                            className={styles.composerInput}
                            autoFocus
                            onEscape={cancelEdit}
                        />
                        <div className={styles.editActions}>
                            <button type="button" className={styles.editSaveBtn} onClick={saveEdit} disabled={busy || !String(editText || '').trim()} aria-label="Save edit">
                                <Checkmark16Regular />
                            </button>
                            <button type="button" className={styles.editCancelBtn} onClick={cancelEdit} aria-label="Cancel edit">
                                <Dismiss16Regular />
                            </button>
                        </div>
                    </>
                ) : (
                    <CommentBody text={item.body} candidates={mentionCandidates} deleted={item.deleted} />
                )}
                {item.editHistory?.length > 0 && !editing && (
                    <details className={styles.editHistory}>
                        <summary>Edit history</summary>
                        {item.editHistory.map((entry, index) => (
                            <p key={`${item.id}-edit-${index}`} className={styles.discBody}>
                                {entry.body}
                                {entry.editedAt ? <span className={styles.discTime}> · {entry.editedAt}</span> : null}
                            </p>
                        ))}
                    </details>
                )}
                {!item.deleted && !editing && !confirmDelete && (
                    <div className={styles.discActions}>
                        {isThread && (
                            <button type="button" className={styles.discAction} onClick={() => onReply(item.id)}>
                                Reply
                            </button>
                        )}
                        {mine && (
                            <>
                                <button type="button" className={styles.discAction} onClick={startEdit}>
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className={styles.discAction}
                                    onClick={() => {
                                        setConfirmDelete(true);
                                        if (replyOpen) onCancelReply?.();
                                    }}>
                                    Delete
                                </button>
                            </>
                        )}
                        <button type="button" className={styles.discAction} onClick={() => onCopy?.(item)}>
                            {copiedCommentId === item.id ? 'Copied' : 'Copy link'}
                        </button>
                        {isThread && !item.resolved && (
                            <button type="button" className={styles.discAction} onClick={() => onResolve?.(item)}>
                                Resolve
                            </button>
                        )}
                        {isThread && item.resolved && (
                            <button type="button" className={styles.discAction} onClick={() => onReopen?.(item)}>
                                Reopen
                            </button>
                        )}
                    </div>
                )}
                {confirmDelete && (
                    <div className={styles.deleteConfirm}>
                        <span>Delete this comment?</span>
                        <button
                            type="button"
                            className={styles.deleteConfirmYes}
                            disabled={busy}
                            onClick={async () => {
                                const deleted = await onConfirmDelete(item);
                                if (deleted !== false) setConfirmDelete(false);
                            }}>
                            Confirm delete
                        </button>
                        <button type="button" className={styles.discAction} onClick={() => setConfirmDelete(false)}>
                            Cancel
                        </button>
                    </div>
                )}
                {replyOpen && isThread && (
                    <div className={styles.replyComposer}>
                        <MentionField
                            value={replyText}
                            onChange={onReplyText}
                            candidates={mentionCandidates}
                            placeholder="Write a reply..."
                            className={styles.composerInput}
                            mentionTrigger={mentionTrigger}
                            autoFocus
                            onEscape={onCancelReply}
                        />
                        <div className={styles.composerBar}>
                            <button type="button" className={styles.mention} onClick={onMentionTrigger}>
                                @ Mention
                            </button>
                            <div className={styles.composerBarRight}>
                                <button type="button" className={styles.discAction} onClick={onCancelReply}>
                                    Cancel
                                </button>
                                <button type="button" className={styles.primaryBtn} onClick={onSubmitReply} disabled={busy || !replyText?.trim()}>
                                    Reply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {item.replies?.length > 0 && (
                    <div className={styles.replies}>
                        {item.replies.map(reply => (
                            <Comment
                                key={reply.id || `${reply.author}-${reply.time}`}
                                item={reply}
                                onReply={onReply}
                                onSaveEdit={onSaveEdit}
                                onConfirmDelete={onConfirmDelete}
                                onCopy={onCopy}
                                copiedCommentId={copiedCommentId}
                                onResolve={onResolve}
                                onReopen={onReopen}
                                viewerMembershipId={viewerMembershipId}
                                replyFor={replyFor}
                                replyText={replyText}
                                onReplyText={onReplyText}
                                onSubmitReply={onSubmitReply}
                                onCancelReply={onCancelReply}
                                mentionCandidates={mentionCandidates}
                                mentionTrigger={mentionTrigger}
                                commentPrefix={commentPrefix}
                                onMentionTrigger={onMentionTrigger}
                                focusCommentId={focusCommentId}
                                isThread={false}
                                busy={busy}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function DiscussionPanel({
    item,
    comment,
    onComment,
    onPostComment,
    mentionCandidates,
    onReply,
    replyFor,
    replyText,
    onReplyText,
    onSubmitReply,
    onEditComment,
    onDeleteComment,
    onCopyComment,
    copiedCommentId,
    onResolveThread,
    onReopenThread,
    focusCommentId,
    commentPrefix = 'irl-comment',
    busy,
}) {
    const [composerMentionTick, setComposerMentionTick] = React.useState(0);
    const [replyMentionTick, setReplyMentionTick] = React.useState(0);
    const candidates = mentionCandidates?.length ? mentionCandidates : item?.mentionCandidates || [];
    const viewer = candidates.find(member => String(member.id) === String(item?.viewerMembershipId));
    const viewerInitials = initialsFromName(viewer?.name);

    React.useEffect(() => {
        if (!focusCommentId) return undefined;
        const node = document.getElementById(`${commentPrefix}-${focusCommentId}`);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return undefined;
    }, [commentPrefix, focusCommentId, item?.discussion]);

    return (
        <div className={styles.detailInner}>
            <h3 className={styles.discussionHeading}>Discussion</h3>
            {(item?.discussion || []).map(entry => (
                <Comment
                    key={entry.id}
                    item={entry}
                    onReply={onReply}
                    onSaveEdit={onEditComment}
                    onConfirmDelete={onDeleteComment}
                    onCopy={onCopyComment}
                    copiedCommentId={copiedCommentId}
                    onResolve={onResolveThread}
                    onReopen={onReopenThread}
                    viewerMembershipId={item.viewerMembershipId}
                    replyFor={replyFor}
                    replyText={replyText}
                    onReplyText={onReplyText}
                    onSubmitReply={onSubmitReply}
                    onCancelReply={() => onReply?.(null)}
                    mentionCandidates={candidates}
                    mentionTrigger={replyFor === entry.id ? replyMentionTick : 0}
                    onMentionTrigger={() => setReplyMentionTick(value => value + 1)}
                    focusCommentId={focusCommentId}
                    commentPrefix={commentPrefix}
                    isThread
                    busy={busy}
                />
            ))}
            {(item?.discussionEvents || []).map(event => (
                <div key={`${event.date}-${event.title}`} className={styles.eventLine}>
                    {event.detail ? `${event.detail} ` : ''}
                    {event.title}
                    {event.time ? ` · ${event.time}` : ''}
                </div>
            ))}
            <div className={styles.composerRow}>
                <div className={styles.avatar}>{viewerInitials}</div>
                <div className={styles.composerGrow}>
                    <MentionField
                        value={comment}
                        onChange={onComment}
                        candidates={candidates}
                        placeholder="Add to the discussion..."
                        className={styles.composerInput}
                        mentionTrigger={composerMentionTick}
                    />
                    <div className={styles.composerBar}>
                        <button type="button" className={styles.mention} onClick={() => setComposerMentionTick(value => value + 1)}>
                            @ Mention
                        </button>
                        <button type="button" className={styles.primaryBtn} onClick={onPostComment} disabled={busy || !comment?.trim()}>
                            Post
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function IRL_Discussion({
    request,
    tab,
    onTab,
    onBack,
    onOpenAi,
    onCopy,
    copied,
    tabs,
    comment,
    onComment,
    onPostComment,
    extraActions,
    onUpdateFields,
    mentionCandidates,
    onReply,
    replyFor,
    replyText,
    onReplyText,
    onSubmitReply,
    onEditComment,
    onDeleteComment,
    onCopyComment,
    copiedCommentId,
    onResolveThread,
    onReopenThread,
    focusCommentId,
    busy,
}) {
    return (
        <>
            <DetailHeader kind="irl" item={request} onBack={onBack} onOpenAi={onOpenAi} onCopy={onCopy} copied={copied} extraActions={extraActions} onUpdateFields={onUpdateFields} busy={busy} />
            <DetailTabs tabs={tabs} tab={tab} onTab={onTab} counts={IrlCounts(request)} />
            <div className={styles.detailBody}>
                <DiscussionPanel
                    item={request}
                    comment={comment}
                    onComment={onComment}
                    onPostComment={onPostComment}
                    mentionCandidates={mentionCandidates}
                    onReply={onReply}
                    replyFor={replyFor}
                    replyText={replyText}
                    onReplyText={onReplyText}
                    onSubmitReply={onSubmitReply}
                    onEditComment={onEditComment}
                    onDeleteComment={onDeleteComment}
                    onCopyComment={onCopyComment}
                    copiedCommentId={copiedCommentId}
                    onResolveThread={onResolveThread}
                    onReopenThread={onReopenThread}
                    focusCommentId={focusCommentId}
                    busy={busy}
                />
            </div>
        </>
    );
}
